'use client';

import { useLocale } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ContentContainer } from '@module/mdrt-org/shared/components/content-container/content-container';
import { Loader } from '@module/mdrt-org/shared/components/loader/loader';
import { Card } from '@shared/ui-components/card/card';
import { SelectField } from '@shared/ui-components/form-fields/select-field';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { type SelectOption } from '@shared/utils/types/select-option';
import { CompanyListingTable } from './components/company-listing-table/company-listing-table';
import { type CompanyListingPageContent, MEMBERSHIP_LEVEL_ALL, type MembershipLevelSelection } from './get-company-listing-page-content';
import { downloadCompanyListingsExcel } from './utils/download-company-listings-excel';
import { PAGE_SIZE } from './utils/get-company-listings/get-company-listing';
import { type CompanyListingResult, CompanyListingType } from './utils/get-company-listings/types';
import { useCompanyListingQuery } from './utils/get-company-listings/use-company-listing-query';
import { useDownloadCompanyListingsQuery } from './utils/get-company-listings/use-download-company-listings-query';
import { getTableData } from './utils/get-table-data';
import styles from './company-listing-page-content-client.module.scss';
const YEAR_OPTIONS_COUNT = 4;
const buildYearOptions = (currentYear: number): Array<SelectOption<number>> => Array.from({
  length: YEAR_OPTIONS_COUNT
}, (_, index) => {
  const year = currentYear - index;
  return {
    label: String(year),
    value: year
  };
});
type CompanyListingsFiltersFormData = {
  availableList: null | SelectOption<CompanyListingType>;
  membershipLevel: null | SelectOption<MembershipLevelSelection>;
  year: null | SelectOption<number>;
};
export const CompanyListingPageContentClient = ({
  cmsContent,
  companyId,
  companyNameWithId,
  currentYear
}: {
  cmsContent: CompanyListingPageContent;
  companyId: number;
  companyNameWithId: string;
  currentYear: number;
}) => {
  const locale = useLocale();
  const {
    listTypeOptions,
    membershipLevelOptions
  } = cmsContent;
  const yearOptions = buildYearOptions(currentYear);
  const formMethods = useForm<CompanyListingsFiltersFormData>({
    ...formBaseConfig,
    defaultValues: {
      availableList: listTypeOptions[0] ?? null,
      membershipLevel: membershipLevelOptions.find(option => option?.value === MEMBERSHIP_LEVEL_ALL) ?? null,
      year: yearOptions[0] ?? null
    }
  });
  const {
    availableList,
    membershipLevel,
    year
  } = formMethods.watch();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{
    ascending: boolean;
    field: string | undefined;
  }>({
    ascending: true,
    field: undefined
  });
  const [dropdownReady, setDropdownReady] = useState(false);
  useEffect(() => {
    // react-select generates random option IDs that don't match between SSR and client,
    // so only render the dropdowns after mount to avoid hydration mismatch.
    setDropdownReady(true);
  }, []);
  const listType = availableList?.value ?? CompanyListingType.APPROVED_MEMBER;
  const membershipLevelValue = membershipLevel?.value;
  const yearValue = year?.value ?? currentYear;
  useEffect(() => {
    setPage(1);
    setSort({
      ascending: true,
      field: undefined
    });
  }, [listType, membershipLevelValue, yearValue]);
  const isMembershipLevelFilterDisabled = listType === CompanyListingType.PENDING_MEMBER;
  const effectiveMembershipLevel = isMembershipLevelFilterDisabled || membershipLevelValue === MEMBERSHIP_LEVEL_ALL ? undefined : membershipLevelValue;
  const listingFilters = useMemo(() => ({
    companyId,
    listType,
    membershipLevel: effectiveMembershipLevel,
    sortAscending: sort.field ? sort.ascending : undefined,
    sortField: sort.field,
    year: yearValue
  }), [companyId, listType, effectiveMembershipLevel, sort.ascending, sort.field, yearValue]);
  const {
    data,
    isError,
    isFetching,
    isLoading
  } = useCompanyListingQuery({
    ...listingFilters,
    pageNumber: page,
    pageSize: PAGE_SIZE
  });
  const {
    cancelDownload,
    fetchAllListings,
    isDownloading
  } = useDownloadCompanyListingsQuery(listingFilters);
  useEffect(() => {
    cancelDownload();
  }, [cancelDownload, listingFilters]);
  const handleSort = (field: string) => {
    setSort(previous => ({
      ascending: previous.field === field ? !previous.ascending : true,
      field
    }));
    setPage(1);
  };
  const buildTableData = (result: CompanyListingResult) => cmsContent.membersTableHeaderLabels ? getTableData(result, cmsContent.membersTableHeaderLabels, cmsContent.statusIdToProblemMap, locale, cmsContent.membershipLevelLabels, cmsContent.membershipTypeLabels) : null;
  const tableData = data ? buildTableData(data) : null;
  const isDownloadDisabled = isFetching || isDownloading;
  const handleDownload = async () => {
    const result_0 = await fetchAllListings();
    const downloadTableData = result_0 ? buildTableData(result_0) : null;
    if (!downloadTableData) {
      return;
    }
    await downloadCompanyListingsExcel(downloadTableData, availableList?.label ?? companyNameWithId, `${companyNameWithId}.xlsx`);
  };
  return <>
      <ContentContainer>
        <div className={styles['filters']}>
          <Card className={styles['company-card']}>
            <h2 className={styles['company-card__name']}>{companyNameWithId}</h2>
          </Card>
          {dropdownReady && <FormWrapper<CompanyListingsFiltersFormData> className={styles['filters__form']} dataTestId="company-listings-filters" formMethods={formMethods} onSubmit={() => undefined}>
              <SelectField hasOptionalLabel={false} label={cmsContent.additionalSearchCriteriaDropDownLabel} name="availableList" options={listTypeOptions} />
              <SelectField hasOptionalLabel={false} isDisabled={isMembershipLevelFilterDisabled} label={cmsContent.membershipLevelDropDownLabel} name="membershipLevel" options={membershipLevelOptions} />
              <SelectField hasOptionalLabel={false} label={cmsContent.yearDropDownLabel} name="year" options={yearOptions} />
            </FormWrapper>}
        </div>
      </ContentContainer>
      {isLoading && <div className={styles['table-loader']}>
          <Loader allowScroll isInner />
        </div>}
      {!isLoading && tableData && tableData?.totalCount > 0 && <CompanyListingTable companyNameWithId={companyNameWithId} currentPage={page} downloadButtonLabel={cmsContent.downloadCompanyListingsLink?.DisplayText ?? undefined} isDownloadDisabled={isDownloadDisabled} isDownloading={isDownloading} onDownload={handleDownload} onPageChange={setPage} onSort={handleSort} recordsFoundText={cmsContent.recordsFoundText} sortAscending={sort.ascending} sortField={sort.field} tableData={tableData} />}
      {!isLoading && tableData?.totalCount === 0 && <div className={styles['no-results-message']}>
          <h3>{cmsContent.noResultsText}</h3>
        </div>}
      {!isLoading && isError && <div className={styles['error-message']}>
          <h3>{cmsContent.errorResultsText}</h3>
        </div>}
    </>;
};